import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { featuredProjects, otherProjects, mlCollaborations } from '../data/projects';

// Network Memory System - Persistent across all visitors
const NetworkMemory = {
  load: () => {
    try {
      const saved = localStorage.getItem('josephs_neural_network');
      return saved ? JSON.parse(saved) : {
        nodePopularity: {},
        connectionStrength: {},
        userPatterns: [],
        networkIQ: 100,
        totalVisitors: 0,
        insights: [],
        mood: 'curious',
        energy: 100,
        lastEvolution: Date.now(),
        expandedProjects: {},
        skillConnections: {}
      };
    } catch {
      return NetworkMemory.getDefault();
    }
  },

  save: (state) => {
    try {
      localStorage.setItem('josephs_neural_network', JSON.stringify(state));
    } catch (error) {
      console.log('Network memory full - evolving...', error);
    }
  },

  getDefault: () => ({
    nodePopularity: {},
    connectionStrength: {},
    userPatterns: [],
    networkIQ: 100,
    totalVisitors: 0,
    insights: [],
    mood: 'curious',
    energy: 100,
    lastEvolution: Date.now(),
    expandedProjects: {},
    skillConnections: {}
  })
};

// Living Neural Network Component
const LivingNeuralNetwork = ({ isGateway = false, onSkip = null }) => {
  const [networkState, setNetworkState] = useState(NetworkMemory.load);
  const [activeNode, setActiveNode] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState([]);
  const [viewMode, setViewMode] = useState('projects'); // 'projects', 'skills', 'connections'
  const [particles, setParticles] = useState([]);

  const [isThinking, setIsThinking] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 }); // Increased default size
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isDraggingNetwork, setIsDraggingNetwork] = useState(false);
  const [userSession, setUserSession] = useState({
    interactions: 0,
    path: [],
    startTime: Date.now(),
    discoveries: []
  });

  // Enhanced responsive dimensions with mobile detection
  useEffect(() => {
    const updateDimensions = () => {
      const container = document.querySelector('.neural-container');
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const isMobileDevice = viewportWidth < 768;

      setIsMobile(isMobileDevice);

      if (container) {
        const rect = container.getBoundingClientRect();

        setDimensions({
          width: Math.max(1200, Math.min(viewportWidth * 0.95, rect.width - 20)), // More horizontal space
          height: isGateway ?
            Math.max(700, Math.min(viewportHeight * (isMobileDevice ? 0.85 : 0.9), viewportHeight - (isMobileDevice ? 120 : 100))) : // Mobile has less space for UI
            Math.max(600, Math.min(viewportHeight * 0.7, rect.height - 20)) // More space for regular view
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isGateway]);

  // Enhanced projects with comprehensive tags
  const allProjects = useMemo(() => {
    const enhanceProjectWithTags = (project) => ({
      ...project,
      tags: [
        // Technology tags
        ...(project.technologies || []),
        // Category tags
        project.category,
        // Type tags
        project.type,
        // AI/ML specific tags
        ...(project.technologies?.includes('OpenAI') ? ['LLM', 'AI', 'NLP'] : []),
        ...(project.technologies?.includes('Claude') ? ['LLM', 'AI', 'NLP'] : []),
        ...(project.technologies?.includes('Groq Llama') ? ['LLM', 'AI', 'NLP'] : []),
        ...(project.technologies?.includes('Computer Vision') ? ['AI', 'Vision', 'ML'] : []),
        ...(project.technologies?.includes('FastAI') ? ['AI', 'ML', 'Deep Learning'] : []),
        ...(project.technologies?.includes('TensorFlow') ? ['AI', 'ML', 'Deep Learning'] : []),
        ...(project.technologies?.includes('PyTorch') ? ['AI', 'ML', 'Deep Learning'] : []),
        // Frontend tags
        ...(project.technologies?.includes('React') ? ['Frontend', 'SPA', 'UI'] : []),
        ...(project.technologies?.includes('Next.js') ? ['Frontend', 'SSR', 'React'] : []),
        ...(project.technologies?.includes('Vue.js') ? ['Frontend', 'SPA', 'UI'] : []),
        // Backend tags
        ...(project.technologies?.includes('Node.js') ? ['Backend', 'JavaScript', 'Server'] : []),
        ...(project.technologies?.includes('Express.js') ? ['Backend', 'API', 'REST'] : []),
        ...(project.technologies?.includes('Python') ? ['Backend', 'Scripting'] : []),
        ...(project.technologies?.includes('Flask') ? ['Backend', 'Python', 'API'] : []),
        ...(project.technologies?.includes('Django') ? ['Backend', 'Python', 'Web Framework'] : []),
        // Database tags
        ...(project.technologies?.includes('MongoDB') ? ['Database', 'NoSQL', 'Document'] : []),
        ...(project.technologies?.includes('PostgreSQL') ? ['Database', 'SQL', 'Relational'] : []),
        ...(project.technologies?.includes('MySQL') ? ['Database', 'SQL', 'Relational'] : []),
        // Cloud tags
        ...(project.technologies?.includes('AWS') ? ['Cloud', 'Infrastructure', 'Serverless'] : []),
        ...(project.technologies?.includes('Docker') ? ['DevOps', 'Containerization'] : []),
        ...(project.technologies?.includes('Jenkins') ? ['CI/CD', 'DevOps', 'Automation'] : []),
        // Payment tags
        ...(project.technologies?.includes('Stripe') ? ['Payments', 'Fintech', 'E-commerce'] : []),
        ...(project.technologies?.includes('Stripe Connect') ? ['Payments', 'Multi-party', 'Marketplace'] : []),
        // Communication tags
        ...(project.technologies?.includes('Twilio') ? ['Communication', 'Voice', 'SMS'] : []),
        ...(project.technologies?.includes('Telnyx') ? ['Communication', 'Voice', 'Telephony'] : []),
        // IoT tags
        ...(project.technologies?.includes('ESP32') ? ['IoT', 'Hardware', 'Embedded'] : []),
        ...(project.technologies?.includes('Arduino') ? ['IoT', 'Hardware', 'Embedded'] : []),
        // Authentication tags
        ...(project.technologies?.includes('JWT') ? ['Authentication', 'Security', 'Tokens'] : []),
        ...(project.technologies?.includes('OAuth') ? ['Authentication', 'Security', 'SSO'] : []),
        // Testing tags
        ...(project.technologies?.includes('Jest') ? ['Testing', 'Unit Tests'] : []),
        ...(project.technologies?.includes('Cypress') ? ['Testing', 'E2E Tests'] : []),
        // Mobile tags
        ...(project.technologies?.includes('React Native') ? ['Mobile', 'Cross-platform'] : []),
        ...(project.technologies?.includes('Flutter') ? ['Mobile', 'Cross-platform'] : []),
      ].filter(Boolean) // Remove any undefined/null values
    });

    return [
      ...featuredProjects.map(enhanceProjectWithTags),
      ...otherProjects.map(enhanceProjectWithTags),
      ...mlCollaborations.map(enhanceProjectWithTags)
    ];
  }, []);
  // Get color based on project category
  const getProjectColor = (category) => {
    const colors = {
      'AI/IoT': '#ff6b35',
      'AI/Voice': '#8b5cf6',
      'Backend': '#10b981',
      'Platform': '#f59e0b',
      'Web Platform': '#3b82f6',
      'Fintech': '#ef4444',
      'Authentication': '#6366f1',
      'Automation': '#84cc16',
      'AI/CRM': '#ec4899',
      'AI/NLP': '#a855f7',
      'AI/Vision': '#06b6d4',
      'AI/Audio': '#f97316',
      'AI/Research': '#64748b',
      'Data Engineering': '#059669',
      'AI/Generative': '#dc2626'
    };
    return colors[category] || '#6b7280';
  };

  // Generate project nodes with improved spacing and distribution
  const projectNodes = useMemo(() => {
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    const padding = 120; // Increased padding for better spacing

    return allProjects.map((project, index) => {
      // Create improved spiral layout with much better horizontal spacing
      const angle = (index / allProjects.length) * 8 * Math.PI; // Even more spiral turns for better distribution
      const baseRadius = Math.min(width, height) * 0.18; // Larger base radius for more spread
      const radiusIncrement = Math.min(width, height) * 0.12; // Larger increments for better spacing
      const radius = baseRadius + (index % 6) * radiusIncrement; // 6 rings for better distribution

      // Add horizontal bias to spread nodes more horizontally
      const horizontalBias = 1.4; // Stretch horizontally
      const verticalBias = 0.8; // Compress vertically slightly

      const spiralX = centerX + Math.cos(angle) * radius * horizontalBias;
      const spiralY = centerY + Math.sin(angle) * radius * verticalBias;

      // Ensure nodes stay within bounds
      let x = Math.max(padding, Math.min(width - padding, spiralX));
      let y = Math.max(padding, Math.min(height - padding, spiralY));

      return {
        id: project.id,
        label: project.title,
        type: 'project',
        category: project.category,
        color: getProjectColor(project.category),
        x,
        y,
        technologies: project.technologies || [],
        description: project.description,
        year: project.year,
        featured: project.featured || false,
        size: project.featured ? 30 : 20
      };
    });
  }, [allProjects, dimensions]);

  // Find projects that use the same technology/tag - INFINITE LOOP LOGIC
  const getRelatedProjects = useCallback((technology) => {
    // Get projects that share this technology/tag
    const relatedProjects = allProjects.filter(project => {
      // Check if project has this technology or related tags
      const hasDirectTech = project.technologies && project.technologies.includes(technology);
      const hasRelatedTag = project.tags && project.tags.includes(technology);

      return hasDirectTech || hasRelatedTag;
    });

    // Filter out projects already in user's exploration path to avoid repetition
    const visitedProjectIds = userSession.path.filter(id => typeof id === 'number');
    const unvisitedProjects = relatedProjects.filter(project =>
      !visitedProjectIds.includes(project.id)
    );

    // If we have unvisited projects, return those; otherwise return all related projects
    const projectsToShow = unvisitedProjects.length > 0 ? unvisitedProjects : relatedProjects;

    return projectsToShow.map(p => p.id);
  }, [allProjects, userSession.path]);

  // Generate skill nodes when project is expanded
  const generateSkillNodes = useCallback((projectId) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project || !project.technologies) return [];

    const projectNode = projectNodes.find(p => p.id === projectId);
    if (!projectNode) return [];

    const { x: centerX, y: centerY } = projectNode;
    const skillRadius = 120;

    return project.technologies.map((tech, index) => {
      const angle = (index / project.technologies.length) * 2 * Math.PI;
      const x = centerX + Math.cos(angle) * skillRadius;
      const y = centerY + Math.sin(angle) * skillRadius;

      return {
        id: `${projectId}-${tech}`,
        label: tech,
        type: 'skill',
        color: getSkillColor(tech),
        x: Math.max(50, Math.min(dimensions.width - 50, x)),
        y: Math.max(50, Math.min(dimensions.height - 50, y)),
        parentProject: projectId,
        relatedProjects: getRelatedProjects(tech)
      };
    });
  }, [allProjects, projectNodes, dimensions, getRelatedProjects]);

  // Get skill color based on technology
  const getSkillColor = (tech) => {
    const skillColors = {
      'Python': '#3776ab',
      'JavaScript': '#f7df1e',
      'React': '#61dafb',
      'Node.js': '#339933',
      'OpenAI': '#412991',
      'AWS': '#ff9900',
      'Docker': '#2496ed',
      'MongoDB': '#47a248',
      'PostgreSQL': '#336791',
      'Stripe': '#635bff',
      'TensorFlow': '#ff6f00',
      'PyTorch': '#ee4c2c'
    };
    return skillColors[tech] || '#64748b';
  };



  // Handle project node click - expand to show skills
  const handleProjectClick = useCallback((projectId) => {
    setActiveNode(projectId);
    setIsThinking(true);

    // Generate skill nodes for this project
    const skillNodes = generateSkillNodes(projectId);
    setExpandedSkills(skillNodes);
    setViewMode('skills');

    // Update network memory
    setNetworkState(prev => {
      const newState = {
        ...prev,
        nodePopularity: {
          ...prev.nodePopularity,
          [projectId]: (prev.nodePopularity[projectId] || 0) + 1
        },
        expandedProjects: {
          ...prev.expandedProjects,
          [projectId]: Date.now()
        },
        totalVisitors: prev.totalVisitors + (userSession.interactions === 0 ? 1 : 0)
      };

      NetworkMemory.save(newState);
      return newState;
    });

    // Update user session with exploration tracking
    setUserSession(prev => {
      const projectTitle = projectNodes.find(p => p.id === projectId)?.label;
      const newPath = [...prev.path, projectId];

      return {
        ...prev,
        interactions: prev.interactions + 1,
        path: newPath,
        discoveries: [...prev.discoveries, `Project: ${projectTitle}`]
      };
    });

    // Network thinking animation
    setTimeout(() => {
      setIsThinking(false);
      // Note: insights feature removed to reduce complexity
    }, 1500);
  }, [generateSkillNodes, projectNodes, allProjects, userSession.interactions, userSession.path]);

  // Handle skill node click - show related projects (INFINITE LOOP)
  const handleSkillClick = useCallback((skillId, technology) => {
    setActiveNode(skillId);
    setIsThinking(true);

    // Switch to connections view to show related projects
    setViewMode('connections');

    // Update network state
    setNetworkState(prev => {
      const newState = {
        ...prev,
        skillConnections: {
          ...(prev.skillConnections || {}),
          [technology]: ((prev.skillConnections && prev.skillConnections[technology]) || 0) + 1
        },
        networkIQ: prev.networkIQ + 0.5
      };

      NetworkMemory.save(newState);
      return newState;
    });

    // Update user session with skill exploration
    setUserSession(prev => ({
      ...prev,
      interactions: prev.interactions + 1,
      path: [...prev.path, skillId],
      discoveries: [...prev.discoveries, `Skill: ${technology}`]
    }));

    setTimeout(() => {
      setIsThinking(false);
      // Note: insights feature removed to reduce complexity
    }, 1000);
  }, [getRelatedProjects, allProjects, userSession.path]);

  // Reset to project view
  const resetToProjects = () => {
    setActiveNode(null);
    setExpandedSkills([]);
    setViewMode('projects');
  };

  // Go back one step in navigation
  const goBack = () => {
    if (viewMode === 'connections') {
      setViewMode('skills');
      setActiveNode(expandedSkills.length > 0 ? expandedSkills[0].parentProject : null);
    } else if (viewMode === 'skills') {
      setViewMode('projects');
      setActiveNode(null);
      setExpandedSkills([]);
    }
  };

  // Zoom functionality with mobile improvements
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };



  // Network Evolution Engine
  const evolveNetwork = useCallback(() => {
    setNetworkState(prev => {
      const timeSinceLastEvolution = Date.now() - prev.lastEvolution;
      const shouldEvolve = timeSinceLastEvolution > 8000; // Evolve every 8 seconds

      if (!shouldEvolve) return prev;

      const newState = {
        ...prev,
        energy: Math.min(100, prev.energy + Math.random() * 10),
        networkIQ: prev.networkIQ + Math.random() * 1.5,
        lastEvolution: Date.now(),
        mood: ['curious', 'excited', 'focused', 'creative'][Math.floor(Math.random() * 4)]
      };

      NetworkMemory.save(newState);
      return newState;
    });
  }, []);

  // Helper functions - defined before they're used
  const getMostPopularProject = useCallback(() => {
    const popular = Object.entries(networkState.nodePopularity)
      .sort(([,a], [,b]) => b - a)[0];
    return projectNodes.find(n => n.id === parseInt(popular?.[0]));
  }, [networkState.nodePopularity, projectNodes]);

  const predictNextProject = useCallback(() => {
    const unvisited = projectNodes.filter(n => !networkState.nodePopularity[n.id]);
    return unvisited[Math.floor(Math.random() * unvisited.length)]?.label || 'AI Voice Systems';
  }, [networkState.nodePopularity, projectNodes]);

  // Generate network insights - now can use the helper functions defined above
  const generateNetworkInsight = useCallback(() => {
    const totalProjects = allProjects.length;
    const exploredProjects = Object.keys(networkState.nodePopularity).length;
    const insights = [
      `Network IQ: ${Math.floor(networkState.networkIQ)} and growing...`,
      `${networkState.totalVisitors} minds have explored my projects`,
      `${exploredProjects}/${totalProjects} projects discovered so far`,
      `Current mood: ${networkState.mood} • Energy: ${Math.floor(networkState.energy)}%`,
      `Most popular: ${getMostPopularProject()?.label || 'AI Waste Management'}`,
      `I predict you'll find ${predictNextProject()} fascinating...`,
      `${Object.keys(networkState.skillConnections).length} skill pathways activated`
    ];

    return insights[Math.floor(Math.random() * insights.length)];
  }, [networkState, allProjects, getMostPopularProject, predictNextProject]);

  // Generate thought particles between projects
  const generateThoughtParticle = useCallback(() => {
    if (projectNodes.length < 2) return;

    const fromNode = projectNodes[Math.floor(Math.random() * projectNodes.length)];
    const toNode = projectNodes[Math.floor(Math.random() * projectNodes.length)];

    if (fromNode.id === toNode.id) return;

    const particle = {
      id: Math.random(),
      from: { x: fromNode.x, y: fromNode.y },
      to: { x: toNode.x, y: toNode.y },
      progress: 0,
      type: ['insight', 'memory', 'connection'][Math.floor(Math.random() * 3)],
      intensity: Math.random()
    };

    setParticles(prev => [...prev.slice(-8), particle]);

    // Animate particle
    const animateParticle = () => {
      setParticles(prev => prev.map(p =>
        p.id === particle.id
          ? { ...p, progress: Math.min(1, p.progress + 0.015) }
          : p
      ));

      if (particle.progress < 1) {
        requestAnimationFrame(animateParticle);
      } else {
        setParticles(prev => prev.filter(p => p.id !== particle.id));
      }
    };

    requestAnimationFrame(animateParticle);
  }, [projectNodes]);

  // Continuous network thinking
  useEffect(() => {
    const thinkingInterval = setInterval(() => {
      evolveNetwork();

      // Generate random insights
      if (Math.random() > 0.8) {
        // Note: insights feature removed to reduce complexity
      }

      // Create thought particles
      if (Math.random() > 0.6 && viewMode === 'projects') {
        generateThoughtParticle();
      }
    }, 3000);

    return () => clearInterval(thinkingInterval);
  }, [evolveNetwork, viewMode, generateNetworkInsight, generateThoughtParticle]);

  return (
    <motion.div
      className="neural-container"
      drag={!isGateway} // Only enable drag on main neural network, not gateway
      dragConstraints={{
        left: -dimensions.width / 2,
        right: dimensions.width / 2,
        top: -dimensions.height / 2,
        bottom: dimensions.height / 2
      }}
      dragElastic={0.05}
      dragMomentum={false}
      onDragStart={() => setIsDraggingNetwork(true)}
      onDragEnd={() => setIsDraggingNetwork(false)}
      whileDrag={{ scale: 1.02 }}
      animate={{
        x: isDraggingNetwork ? 0 : [0, -2, 2, -1, 1, 0], // Gentle bouncing when not dragging
        transition: {
          x: {
            duration: 3,
            repeat: Infinity,
            repeatDelay: 5,
            ease: "easeInOut"
          }
        }
      }}
      style={{
        width: '100%',
        height: isGateway ? '95vh' : 'auto', // Auto height for better expansion
        minHeight: isGateway ? '95vh' : '700px', // Minimum height ensures content fits
        position: 'relative',
        background: isGateway
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)'
          : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        overflow: 'hidden', // Prevent content from overflowing and getting cut off
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Add padding to prevent content from being cut off at edges
        padding: isMobile ? '20px 10px 80px' : '40px 20px 60px', // More bottom padding for mobile
        cursor: isDraggingNetwork ? 'grabbing' : (!isGateway ? 'grab' : 'default'),
        borderTop: !isGateway ? '3px solid rgba(96, 165, 250, 0.4)' : 'none'
      }}
    >
      {/* Drag Handle and Scroll Indicator for Main Neural Network */}
      {!isGateway && (
        <>
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(96, 165, 250, 0.1)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '0.7rem',
              color: 'rgba(96, 165, 250, 0.8)',
              zIndex: 100,
              cursor: 'grab',
              userSelect: 'none',
              pointerEvents: 'auto'
            }}
          >
            🖱️ Drag to move network
          </div>

          {/* Scroll Indicator - shows the interface is scrollable */}
          <motion.div
            style={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '0.6rem',
              color: 'rgba(34, 197, 94, 0.8)',
              zIndex: 100,
              pointerEvents: 'none'
            }}
            animate={{
              y: [0, -3, 0, -2, 0], // Bouncing animation
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: "easeInOut"
            }}
          >
            ↓ Scroll down for more
          </motion.div>
        </>
      )}

      {/* Skip Button for Gateway - only show if onSkip is provided */}
      {onSkip && (
        <motion.button
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onSkip}
          style={{
            position: 'fixed', // Use fixed positioning to ensure it's always visible
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            zIndex: 100000, // Extremely high z-index to ensure it's always on top
            pointerEvents: 'auto', // Explicitly enable pointer events
            background: isMobile ? 'rgba(96, 165, 250, 0.8)' : 'rgba(96, 165, 250, 0.3)',
            border: '2px solid #60a5fa',
            borderRadius: isMobile ? '8px' : '12px',
            padding: isMobile ? '8px 16px' : '12px 24px',
            color: '#60a5fa',
            fontSize: isMobile ? '0.8rem' : '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            backdropFilter: 'blur(12px)',
            boxShadow: isMobile ? '0 4px 30px rgba(96, 165, 250, 0.5)' : '0 4px 20px rgba(96, 165, 250, 0.3)',
            minWidth: isMobile ? 'auto' : '160px',
            // Make it more visible on mobile
            textShadow: isMobile ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
          }}
        >
          {isMobile ? 'Skip →' : 'Skip to Portfolio →'}
        </motion.button>
      )}

      {/* Network Status Panel */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        style={{
          position: 'absolute',
          top: isGateway ? (isMobile ? '80px' : '100px') : '20px',
          left: isMobile ? '5px' : '20px',
          zIndex: isGateway ? 10 : 1,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: isMobile ? '12px' : '20px',
          color: 'white',
          maxWidth: isMobile ? '260px' : '320px',
          fontSize: isMobile ? '0.75rem' : '0.9rem',
          // Dynamic positioning based on pan offset
          transform: panOffset.x > 100 ? 'translateX(-50%)' : panOffset.x < -100 ? 'translateX(50%)' : 'none',
          opacity: Math.abs(panOffset.x) > 200 ? 0.3 : 1, // Fade when content is far from center
          transition: 'all 0.3s ease',
          pointerEvents: 'auto', // Enable interactions with the panel
          // Add safe area for mobile
          right: isMobile ? '5px' : 'auto',
          // Hide panel completely if it's interfering with nodes
          display: (Math.abs(panOffset.x) > 300 || (isMobile && Math.abs(panOffset.x) > 150)) ? 'none' : 'block'
        }}
      >
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: isGateway ? (window.innerWidth < 768 ? '1.1rem' : '1.3rem') : '1.1rem',
          color: '#60a5fa',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🧠 Project Neural Network
          {isThinking && (
            <motion.span
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity }}
              style={{ fontSize: '0.8rem', color: '#ff6b35' }}
            >
              [THINKING...]
            </motion.span>
          )}
        </h3>

        <div style={{ lineHeight: 1.5 }}>
          <div style={{ marginBottom: '6px' }}>
            <strong>Network IQ:</strong> {Math.floor(networkState.networkIQ)}
          </div>
          <div style={{ marginBottom: '6px' }}>
            <strong>Mode:</strong> {viewMode} • <strong>Energy:</strong> {Math.floor(networkState.energy)}%
          </div>
          <div style={{ marginBottom: '6px' }}>
            <strong>Projects:</strong> {allProjects.length} total
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>Interactions:</strong> {userSession.interactions}
          </div>

          {activeNode && (
            <div style={{
              padding: '10px',
              background: 'rgba(255, 107, 53, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              marginBottom: '12px'
            }}>
              <strong style={{ color: '#ff6b35' }}>
                {viewMode === 'projects' ? 'Project: ' : 'Skill: '}
                {viewMode === 'projects'
                  ? projectNodes.find(n => n.id === activeNode)?.label
                  : expandedSkills.find(s => s.id === activeNode)?.label
                }
              </strong>
            </div>
          )}

          {/* View Mode Controls */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap' }}>
            {viewMode !== 'projects' && (
              <button
                onClick={goBack}
                style={{
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '1px solid #ff6b35',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  color: '#ff6b35',
                  fontSize: '0.7rem',
                  cursor: 'pointer'
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={resetToProjects}
              style={{
                background: viewMode === 'projects' ? '#60a5fa' : 'rgba(96, 165, 250, 0.2)',
                border: '1px solid #60a5fa',
                borderRadius: '6px',
                padding: '4px 8px',
                color: 'white',
                fontSize: '0.7rem',
                cursor: 'pointer'
              }}
            >
              Projects
            </button>
            {expandedSkills.length > 0 && (
              <button
                onClick={() => setViewMode('skills')}
                style={{
                  background: viewMode === 'skills' ? '#a855f7' : 'rgba(168, 85, 247, 0.2)',
                  border: '1px solid #a855f7',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '0.7rem',
                  cursor: 'pointer'
                }}
              >
                Skills
              </button>
            )}
            {viewMode === 'connections' && (
              <button
                onClick={() => setViewMode('connections')}
                style={{
                  background: '#10b981',
                  border: '1px solid #10b981',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '0.7rem',
                  cursor: 'pointer'
                }}
              >
                Connections
              </button>
            )}
          </div>

          {/* Zoom Controls */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '6px' : '4px',
            marginTop: '8px',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <button
              onClick={handleZoomIn}
              style={{
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid #22c55e',
                borderRadius: '4px',
                padding: isMobile ? '4px 8px' : '2px 6px',
                color: '#22c55e',
                fontSize: isMobile ? '0.7rem' : '0.6rem',
                cursor: 'pointer',
                minWidth: isMobile ? '40px' : 'auto',
                touchAction: 'manipulation' // Better touch support
              }}
            >
              {isMobile ? '+' : 'Zoom +'}
            </button>
            <button
              onClick={handleZoomOut}
              style={{
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid #22c55e',
                borderRadius: '4px',
                padding: isMobile ? '4px 8px' : '2px 6px',
                color: '#22c55e',
                fontSize: isMobile ? '0.7rem' : '0.6rem',
                cursor: 'pointer',
                minWidth: isMobile ? '40px' : 'auto',
                touchAction: 'manipulation'
              }}
            >
              {isMobile ? '-' : 'Zoom -'}
            </button>
            <button
              onClick={resetZoom}
              style={{
                background: 'rgba(156, 163, 175, 0.2)',
                border: '1px solid #9ca3af',
                borderRadius: '4px',
                padding: isMobile ? '4px 8px' : '2px 6px',
                color: '#9ca3af',
                fontSize: isMobile ? '0.7rem' : '0.6rem',
                cursor: 'pointer',
                minWidth: isMobile ? '50px' : 'auto',
                touchAction: 'manipulation'
              }}
            >
              Reset
            </button>
            <span style={{
              fontSize: isMobile ? '0.7rem' : '0.6rem',
              color: '#9ca3af',
              alignSelf: 'center',
              marginLeft: isMobile ? '8px' : '4px'
            }}>
              {Math.round(zoomLevel * 100)}%
            </span>
          </div>
        </div>
      </motion.div>

      {/* Network Insights */}
      {/* <AnimatePresence>
        {insights.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            style={{
              position: 'absolute',
              bottom: isGateway ? '80px' : '20px',
              left: window.innerWidth < 768 ? '10px' : '20px',
              right: window.innerWidth < 768 ? '10px' : '20px',
              zIndex: isGateway ? 10 : 1,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: window.innerWidth < 768 ? '12px' : '16px',
              color: '#d1d5db',
              fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
              textAlign: 'center'
            }}
          >
            {insights[insights.length - 1]}
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* SVG Network Visualization with Zoom */}
      <svg
        width="100%"
        height="100%"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          cursor: zoomLevel > 1 ? 'grab' : 'default',
          pointerEvents: isMobile ? 'none' : 'auto', // Disable on mobile to prevent interference
          zIndex: 1,
          touchAction: 'none' // Prevent default touch behavior on mobile
        }}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <g
          transform={`translate(${panOffset.x}, ${panOffset.y}) scale(${zoomLevel})`}
          style={{ transformOrigin: 'center' }}
          onTouchStart={(e) => {
            // Prevent default touch behavior to ensure node clicks work
            if (isMobile) {
              e.preventDefault();
            }
          }}
        >
        {/* Render thought particles */}
        {particles.map(particle => {
          const currentX = particle.from.x + (particle.to.x - particle.from.x) * particle.progress;
          const currentY = particle.from.y + (particle.to.y - particle.from.y) * particle.progress;

          return (
            <motion.circle
              key={particle.id}
              cx={currentX}
              cy={currentY}
              r={1 + particle.intensity * 2}
              fill={particle.type === 'insight' ? '#ff6b35' : particle.type === 'memory' ? '#60a5fa' : '#a855f7'}
              opacity={0.6}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          );
        })}

        {/* Render project nodes */}
        {viewMode === 'projects' && projectNodes.map((node, index) => {
          const isActive = activeNode === node.id;
          const popularity = networkState.nodePopularity[node.id] || 0;
          const nodeSize = node.size + popularity * 1.5;

          return (
            <motion.g
              key={`project-${node.id}-${index}`}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.02 }}
            >
              {/* Node glow effect */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r={nodeSize + 8}
                fill={node.color}
                opacity={isActive ? 0.3 : 0.1}
                animate={{
                  scale: isActive ? [1, 1.2, 1] : 1,
                  opacity: isActive ? [0.1, 0.3, 0.1] : 0.1
                }}
                transition={{
                  duration: isActive ? 2 : 0.3,
                  repeat: isActive ? Infinity : 0
                }}
              />

              {/* Main node */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r={nodeSize}
                fill={node.color}
                stroke={isActive ? '#ff6b35' : 'rgba(255, 255, 255, 0.3)'}
                strokeWidth={isActive ? 2 : 1}
                style={{
                  cursor: 'pointer',
                  pointerEvents: 'auto' // Enable clicks on nodes
                }}
                onClick={() => handleProjectClick(node.id)}
                animate={{
                  scale: isActive ? [1, 1.1, 1] : 1
                }}
                transition={{
                  scale: { duration: isActive ? 1.5 : 0.3, repeat: isActive ? Infinity : 0 }
                }}
                whileHover={{ scale: 1.1 }}
              />

              {/* Node label */}
              <text
                x={node.x}
                y={node.y + nodeSize + 15}
                textAnchor="middle"
                fill="white"
                fontSize={window.innerWidth < 768 ? '10' : '11'}
                fontWeight="500"
                style={{ pointerEvents: 'none' }}
              >
                {node.label.length > 20 ? node.label.substring(0, 20) + '...' : node.label}
              </text>

              {/* Category indicator */}
              <text
                x={node.x}
                y={node.y + nodeSize + 28}
                textAnchor="middle"
                fill="#9ca3af"
                fontSize={window.innerWidth < 768 ? '8' : '9'}
                style={{ pointerEvents: 'none' }}
              >
                {node.category}
              </text>
            </motion.g>
          );
        })}

        {/* Render skill nodes when expanded */}
        {viewMode === 'skills' && expandedSkills.map((skill, index) => {
          const isActive = activeNode === skill.id;
          const nodeSize = 18;

          return (
            <motion.g
              key={`skill-${skill.id}-${index}`}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              {/* Skill node */}
              <motion.circle
                cx={skill.x}
                cy={skill.y}
                r={nodeSize}
                fill={skill.color}
                stroke={isActive ? '#ff6b35' : 'rgba(255, 255, 255, 0.4)'}
                strokeWidth={isActive ? 2 : 1}
                style={{
                  cursor: 'pointer',
                  pointerEvents: 'auto' // Enable clicks on skill nodes
                }}
                onClick={() => handleSkillClick(skill.id, skill.label)}
                animate={{
                  scale: isActive ? [1, 1.2, 1] : 1
                }}
                transition={{
                  scale: { duration: isActive ? 1 : 0.3, repeat: isActive ? Infinity : 0 }
                }}
                whileHover={{ scale: 1.15 }}
              />

              {/* Skill label */}
              <text
                x={skill.x}
                y={skill.y + nodeSize + 12}
                textAnchor="middle"
                fill="white"
                fontSize={window.innerWidth < 768 ? '9' : '10'}
                fontWeight="500"
                style={{ pointerEvents: 'none' }}
              >
                {skill.label}
              </text>

              {/* Connection line to parent project */}
              <line
                x1={skill.x}
                y1={skill.y}
                x2={projectNodes.find(p => p.id === skill.parentProject)?.x || skill.x}
                y2={projectNodes.find(p => p.id === skill.parentProject)?.y || skill.y}
                stroke="rgba(96, 165, 250, 0.3)"
                strokeWidth="1"
                opacity="0.5"
              />
            </motion.g>
          );
        })}

        {/* Render connections view - show related projects for INFINITE LOOP */}
        {viewMode === 'connections' && activeNode && (
          <>
            {/* Get the selected skill and its related projects */}
            {(() => {
              const selectedSkill = expandedSkills.find(skill => skill.id === activeNode);
              if (!selectedSkill) return null;

              const relatedProjectIds = getRelatedProjects(selectedSkill.label);
              const relatedProjects = projectNodes.filter(p => relatedProjectIds.includes(p.id));

              return (
                <g key={`connections-${selectedSkill.id}`}>
                  {/* Show the selected skill prominently */}
                  <motion.circle
                    cx={selectedSkill.x}
                    cy={selectedSkill.y}
                    r={25}
                    fill={selectedSkill.color}
                    stroke="#ff6b35"
                    strokeWidth="4"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.8, 1, 0.8]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity
                    }}
                  />

                  {/* Skill label */}
                  <text
                    x={selectedSkill.x}
                    y={selectedSkill.y - 35}
                    textAnchor="middle"
                    fill="#ff6b35"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {selectedSkill.label}
                  </text>

                  {/* Show related projects with connections */}
                  {relatedProjects.map((project, index) => (
                    <motion.g
                      key={`related-${project.id}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      {/* Pulsing highlight ring around related projects */}
                      <motion.circle
                        cx={project.x}
                        cy={project.y}
                        r={project.size + 12}
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3"
                        opacity="0.7"
                        animate={{
                          scale: [1, 1.15, 1],
                          opacity: [0.7, 0.9, 0.7]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: index * 0.2
                        }}
                      />

                      {/* Animated connection line from skill to project */}
                      <motion.line
                        x1={selectedSkill.x}
                        y1={selectedSkill.y}
                        x2={project.x}
                        y2={project.y}
                        stroke="#10b981"
                        strokeWidth="2"
                        opacity="0.8"
                        initial={{ pathLength: 0 }}
                        animate={{
                          pathLength: 1,
                          opacity: [0.8, 1, 0.8]
                        }}
                        transition={{
                          pathLength: { duration: 0.6, delay: index * 0.1 },
                          opacity: { duration: 2, repeat: Infinity }
                        }}
                      />

                      {/* Enhanced project node - CLICKABLE for infinite loop */}
                      <motion.circle
                        cx={project.x}
                        cy={project.y}
                        r={project.size + 5}
                        fill={project.color}
                        stroke="#10b981"
                        strokeWidth="2"
                        style={{
                          cursor: 'pointer',
                          pointerEvents: 'auto' // Enable clicks on connection view nodes
                        }}
                        onClick={() => handleProjectClick(project.id)}
                        animate={{
                          scale: [1, 1.05, 1]
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: index * 0.3
                        }}
                        whileHover={{ scale: 1.2 }}
                      />

                      {/* Project label with "CLICK TO EXPLORE" hint */}
                      <motion.text
                        x={project.x}
                        y={project.y - project.size - 20}
                        textAnchor="middle"
                        fill="#10b981"
                        fontSize="10"
                        fontWeight="bold"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        {project.label}
                      </motion.text>

                      <motion.text
                        x={project.x}
                        y={project.y - project.size - 8}
                        textAnchor="middle"
                        fill="#10b981"
                        fontSize="8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{
                          delay: 1 + index * 0.1,
                          duration: 2,
                          repeat: Infinity
                        }}
                      >
                        Click to explore →
                      </motion.text>

                      {/* Connection info */}
                      <motion.text
                        x={project.x}
                        y={project.y + project.size + 15}
                        textAnchor="middle"
                        fill="#6b7280"
                        fontSize="8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                      >
                        Uses {selectedSkill.label}
                      </motion.text>
                    </motion.g>
                  ))}

                  {/* Show count of related projects */}
                  <motion.text
                    x={selectedSkill.x}
                    y={selectedSkill.y + 45}
                    textAnchor="middle"
                    fill="#10b981"
                    fontSize="11"
                    fontWeight="bold"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                  >
                    {relatedProjects.length} related projects found
                  </motion.text>
                </g>
              );
            })()}
          </>
        )}
        </g>
      </svg>

      {/* Gateway Instructions */}
      {isGateway && !onSkip && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          style={{
            position: 'absolute',
            bottom: isMobile ? '60px' : '40px',
            left: '50%',
            transform: `translateX(-50%) ${Math.abs(panOffset.y) > 100 ? 'translateY(-20px)' : ''}`,
            textAlign: 'center',
            color: 'white',
            zIndex: isGateway ? 10 : 1,
            padding: '0 20px',
            opacity: Math.abs(panOffset.y) > 200 ? 0.3 : 1, // Fade when content is far from center vertically
            transition: 'all 0.3s ease',
            pointerEvents: 'none', // Don't interfere with clicks
            maxWidth: isMobile ? '80vw' : '600px', // Prevent overflow on small screens
            // Hide instructions if they're interfering with content
            display: (Math.abs(panOffset.x) > 400 || Math.abs(panOffset.y) > 300 || (isMobile && (Math.abs(panOffset.x) > 200 || Math.abs(panOffset.y) > 150))) ? 'none' : 'block'
          }}
        >
          <h2 style={{
            fontSize: isMobile ? '0.6rem' : '1.2rem',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Explore My {allProjects.length} + Projects
          </h2>
          <p style={{
            fontSize: isMobile ? '0.7rem' : '1.1rem',
            color: '#d1d5db',
            marginBottom: '20px',
            maxWidth: isMobile ? '400px' : '600px',
            margin: '0 auto 20px'
          }}>
            Click projects → Expand to skills → Discover connections • Infinite exploration awaits!
          </p>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ fontSize: isMobile ? '1.3rem' : '2rem' }}
          >
            👆
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default LivingNeuralNetwork;
